import React, {FC, useCallback} from "react";
import {useDispatch, useSelector} from "react-redux";
import {get} from "lodash";
import {useTranslation} from "react-i18next";
import {getIsOpenPlayerDetailsModal, getPlayerById, getSquadById} from "modules/selectors";
import {closePlayerDetailsModal, openPlayerDetailsModal} from "modules/actions";
import {
	getPlayerAvatarSrc,
	getPlayerFullName,
	getPlayerName,
	getSquadKey,
} from "modules/utils/helpers";
import {DetailsModalTabs} from "modules/enums";
import {playerPositions} from "modules/constants";
import {IPlayer} from "modules/types";
import {BaseModal} from "components/Modals/BaseModal";
import {PlayerDetailsInfo} from "./PlayerDetailsInfo";
import {PlayerTabs} from "./Tabs";
import {ActionsContainer} from "./common";

interface IPlayerDetailsModal {
	PlayerAction?: React.FC<{player: IPlayer; closeHandler: () => void; isDisabled: boolean}>;
	isDisabled: boolean;
}

export const PlayerDetailsModal: FC<IPlayerDetailsModal> = ({PlayerAction, isDisabled}) => {
	const dispatch = useDispatch();
	const {t} = useTranslation();
	const data = useSelector(getIsOpenPlayerDetailsModal);
	const playerId = data?.id;
	const player = useSelector(getPlayerById)(playerId);
	const squad = useSelector(getSquadById)(player?.squadId);

	const setActiveTab = useCallback(
		(tab: DetailsModalTabs) => {
			if (!data) {
				return;
			}

			dispatch(openPlayerDetailsModal({id: data.id, tab}));
		},
		[dispatch, data]
	);

	const closeHandler = useCallback(() => {
		dispatch(closePlayerDetailsModal());
	}, [dispatch]);

	if (!player || !data) {
		return null;
	}

	const activeTab = data.tab;
	const title = t(get(playerPositions, player.position, ""));

	return (
		<BaseModal
			handleClose={closeHandler}
			className="p-0 above-player-pool"
			overlayClassName="above-player-pool">
			<PlayerDetailsInfo
				name={getPlayerFullName(player)}
				src={getPlayerAvatarSrc(player)}
				alt={getPlayerName(player)}
				title={title}
				squadName={t(getSquadKey(squad))}
			/>

			<PlayerTabs player={player} activeTab={activeTab} onTabChange={setActiveTab} />

			{PlayerAction && (
				<ActionsContainer>
					<PlayerAction
						player={player}
						closeHandler={closeHandler}
						isDisabled={isDisabled}
					/>
				</ActionsContainer>
			)}
		</BaseModal>
	);
};
